import type { RegionId } from './tax-engine';

/**
 * Whether a venue is located in Italy. Used to gate the pay-transparency salary
 * flow (D.Lgs. 96/2026): Italian venues get the gross-estimate flow, everyone
 * else keeps the production flow.
 *
 * The backend only exposes `country` as a free-form string, so we normalize
 * (lowercase + trim) and match the known Italian spellings. An absent/unknown
 * country is treated as NON-Italian (foreign flow) — the safe default, since we
 * can't guarantee the Italian tax estimate applies without a known country.
 *
 * NOTE: kept as a small local twin of `isItalianVenue` in
 * `@restworld/utils-common/utils/venue` (used by the worker-app salary card).
 * Importing the shared one here would drag the whole utils-common barrel (React
 * hooks included) into this pure salary lib and break its unit tests. If the
 * Italian-spelling list changes, update both.
 */
export const isItalianVenue = (country?: string | null): boolean => {
  const normalized = country?.toLowerCase().trim();
  return normalized === 'italia' || normalized === 'italy';
};

/**
 * Default region for the salary tax estimate. The onboarding has no reliable
 * region signal for the venue, so estimates fall back to the national average.
 */
export const DEFAULT_TAX_REGION: RegionId = 'media';

/**
 * Whether the Italian pay-transparency (D.Lgs. 96/2026) gross-estimate flow should run for a
 * given venue. It's a legal requirement for Italian venues, not a feature we toggle on and off,
 * so it runs for every Italian venue (no feature flag / env var).
 */
export const isItalianPayTransparencyFlow = (country?: string | null): boolean => isItalianVenue(country);
